import {
  isSharesProject,
  type Project,
} from "@tudigo-monorepo/core-tudigo-api-models";
import { formatCurrency } from "@tudigo-monorepo/core-tudigo-utils";
import {
  ComparatorSelectionList,
  ComparatorTable,
  ComparatorTableContent,
  ComparatorTableRow,
  ComparatorTableRowLabel,
  ComparatorTableRowValue,
  ComparatorTableTooltip,
  ComparatorTableTrigger,
} from "@tudigo-monorepo/web-tudigo-comparator";

import { getProjectInternalRateOfReturn } from "../../../../../shared-internal/utils/get-project-internal-rate-of-return";

export function ProjectsComparatorDealTable() {
  return (
    <ComparatorTable defaultOpen>
      <ComparatorTableTrigger>Deal</ComparatorTableTrigger>
      <ComparatorTableContent>
        <ComparatorTableRow>
          <ComparatorTableRowLabel>
            Valo pre-money
            <ComparatorTableTooltip>
              Évaluation d&apos;une entreprise avant l&apos;entrée de nouveaux
              capitaux lors d&apos;un tour de financement.
            </ComparatorTableTooltip>
          </ComparatorTableRowLabel>
          <ComparatorSelectionList>
            {(project: Project) => (
              <ComparatorTableRowValue loaderClassName="w-1/2">
                {project.information?.preMoneyValuationInCents
                  ? formatCurrency(project.information.preMoneyValuationInCents)
                  : "N/A"}
              </ComparatorTableRowValue>
            )}
          </ComparatorSelectionList>
        </ComparatorTableRow>

        <ComparatorTableRow>
          <ComparatorTableRowLabel>
            Horizon de sortie
            <ComparatorTableTooltip>
              Délai contractuel de cession de l&apos;investissement.
            </ComparatorTableTooltip>
          </ComparatorTableRowLabel>
          <ComparatorSelectionList>
            {(project: Project) => (
              <ComparatorTableRowValue loaderClassName="w-1/3">
                {isSharesProject(project) &&
                project.sharesMechanicSettings.exitHorizon ? (
                  <>
                    {project.sharesMechanicSettings.exitHorizon.minInMonths ||
                      0}{" "}
                    à{" "}
                    {project.sharesMechanicSettings.exitHorizon.maxInMonths ||
                      0}{" "}
                    mois
                  </>
                ) : (
                  "N/A"
                )}
              </ComparatorTableRowValue>
            )}
          </ComparatorSelectionList>
        </ComparatorTableRow>

        <ComparatorTableRow>
          <ComparatorTableRowLabel>
            Multiple cible
            <ComparatorTableTooltip>
              Montant cible réalisé à la sortie divisé par le montant investi.
            </ComparatorTableTooltip>
          </ComparatorTableRowLabel>
          <ComparatorSelectionList>
            {(project: Project) => (
              <ComparatorTableRowValue loaderClassName="w-[12%] min-w-7">
                {isSharesProject(project) &&
                project.sharesMechanicSettings?.targetedYield?.maxInPercent !==
                  undefined
                  ? `x ${
                      project.sharesMechanicSettings.targetedYield
                        .maxInPercent / 100
                    }`
                  : "N/A"}
              </ComparatorTableRowValue>
            )}
          </ComparatorSelectionList>
        </ComparatorTableRow>

        <ComparatorTableRow>
          <ComparatorTableRowLabel>TRI cible</ComparatorTableRowLabel>
          <ComparatorSelectionList>
            {(project: Project) => {
              const internalRateOfReturn =
                getProjectInternalRateOfReturn(project);

              if (internalRateOfReturn === null) {
                return (
                  <ComparatorTableRowValue loaderClassName="w-1/3">
                    N/A
                  </ComparatorTableRowValue>
                );
              }

              const { value, scenario } = internalRateOfReturn;

              return (
                <ComparatorTableRowValue loaderClassName="w-1/3">
                  {value && value > 0 ? (
                    <>
                      {`${value / 100}%`}
                      <ComparatorTableTooltip>
                        Taux de rendement interne, ou rentabilité annuelle
                        potentielle d&apos;un investissement. On se base ici sur
                        le scenario{" "}
                        {scenario === "middle" ? "middle" : "achat capitalisé"}.
                      </ComparatorTableTooltip>
                    </>
                  ) : (
                    "N/A"
                  )}
                </ComparatorTableRowValue>
              );
            }}
          </ComparatorSelectionList>
        </ComparatorTableRow>
        <ComparatorTableRow>
          <ComparatorTableRowLabel>
            Niveau de risque
            <ComparatorTableTooltip>
              Le potentiel de rendement a pour corolaire un risque de perte
              totale ou partielle du capital investit. L&apos;analyse financière
              proposée sur la page projet et dans la note d&apos;investissement
              détaille le calcul effectué par nos analystes pour chaque levée.
            </ComparatorTableTooltip>
          </ComparatorTableRowLabel>
          <ComparatorSelectionList>
            {(project: Project) => (
              <ComparatorTableRowValue loaderClassName="w-1/6 min-w-6">
                {project.riskInPercent
                  ? `${project.riskInPercent / 10}/10`
                  : "N/A"}
              </ComparatorTableRowValue>
            )}
          </ComparatorSelectionList>
        </ComparatorTableRow>

        <ComparatorTableRow>
          <ComparatorTableRowLabel>
            Montant du tour de table
            <ComparatorTableTooltip>
              Somme totale des fonds levés auprès de la totalité des
              investisseurs lors d&apos;un tour de financement.
            </ComparatorTableTooltip>
          </ComparatorTableRowLabel>
          <ComparatorSelectionList>
            {(project: Project) => (
              <ComparatorTableRowValue loaderClassName="w-2/5">
                {project.information?.roundTableAmountInCents
                  ? formatCurrency(project.information.roundTableAmountInCents)
                  : "N/A"}
              </ComparatorTableRowValue>
            )}
          </ComparatorSelectionList>
        </ComparatorTableRow>

        <ComparatorTableRow>
          <ComparatorTableRowLabel>
            Allocation Tudigo
            <ComparatorTableTooltip>
              Part des fonds investis par la communauté Tudigo dans un tour de
              table.
            </ComparatorTableTooltip>
          </ComparatorTableRowLabel>
          <ComparatorSelectionList>
            {(project: Project) => (
              <ComparatorTableRowValue loaderClassName="w-3/5">
                {project.goal?.maxInCents
                  ? formatCurrency(project.goal?.maxInCents)
                  : "N/A"}
              </ComparatorTableRowValue>
            )}
          </ComparatorSelectionList>
        </ComparatorTableRow>

        <ComparatorTableRow>
          <ComparatorTableRowLabel>
            Co-investisseurs
            <ComparatorTableTooltip>
              Autres investisseurs sur ce tour de table.
            </ComparatorTableTooltip>
          </ComparatorTableRowLabel>
          <ComparatorSelectionList>
            {(project: Project) => (
              <ComparatorTableRowValue>
                {project.content?.coInvestors
                  ? project.content?.coInvestors
                  : "N/A"}
              </ComparatorTableRowValue>
            )}
          </ComparatorSelectionList>
        </ComparatorTableRow>

        <ComparatorTableRow>
          <ComparatorTableRowLabel>Analyse Tudigo</ComparatorTableRowLabel>
          <ComparatorSelectionList>
            {(project: Project) => (
              <ComparatorTableRowValue>
                {project.content?.tudigoAnalysis
                  ? project.content?.tudigoAnalysis
                  : "N/A"}
              </ComparatorTableRowValue>
            )}
          </ComparatorSelectionList>
        </ComparatorTableRow>
      </ComparatorTableContent>
    </ComparatorTable>
  );
}
